import { Injectable, computed, inject, signal } from '@angular/core';

import { CompanyApiService } from 'src/app/api/services/company-api.service';
import { getError } from 'src/app/api/services/api-utils.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { ExceptionLoggingService } from 'src/app/components/logging/exception-logging.service';
import {
  CompanySelectedSource,
  TrackerService,
} from 'src/app/components/logging/tracker.service';
import { ListErrorHandlerService } from 'src/app/components/scrolling-list/services/list-error-handler.service';
import { AddressUtilsService } from 'src/app/shared/services/address-utils.service';
import { CompanyTreeCacheService } from './company-tree-cache.service';
import { CompanyTreeExpansionService } from './company-tree-expansion.service';
import { CompanyTreeViewModeService } from './company-tree-view-mode.service';
import {
  BranchState,
  BranchStatus,
  CompanyNode,
  CompanyTreeRow,
} from 'src/app/app-common/types/company-tree';

const BRANCH_PAGE_SIZE = 40;

const MISSING_USER_COMPANY_ERROR =
  'Your company could not be loaded. Please reload this page and try again.';

type CompanyLike = {
  id?: string;
  name?: string;
  parentId?: string | null;
  street?: string | null;
  city?: string | null;
  province?: string | null;
  displayCount?: number;
};

@Injectable()
export class CompanyTreeService {
  private readonly cache = inject(CompanyTreeCacheService);
  private readonly companyApi = inject(CompanyApiService);
  private readonly companyState = inject(CompanyStateService);
  private readonly expansion = inject(CompanyTreeExpansionService);
  private readonly viewMode = inject(CompanyTreeViewModeService);
  private readonly addressUtils = inject(AddressUtilsService);
  private readonly exceptionLogging = inject(ExceptionLoggingService);
  private readonly listErrorHandler = inject(ListErrorHandlerService);
  private readonly tracker = inject(TrackerService);

  // Remembered between panel openings; everything below is per-opening state.
  private readonly nodes = this.cache.nodes;
  private readonly branches = this.cache.branches;
  private readonly knownLeaves = this.cache.knownLeaves;

  private readonly expanded = signal(new Set<string>());
  private readonly selectedPath = signal<string[]>([]);
  private readonly userCompanyId = signal<string | null>(null);
  private readonly selectedCompanyId = signal<string | null>(null);
  private readonly initialStatusState = signal<BranchStatus>('idle');
  private readonly initialError = signal<string | null>(null);
  private readonly showAllCompaniesState = signal(false);

  private resolvingAncestors = false;

  readonly initialStatus = this.initialStatusState.asReadonly();
  readonly initialErrorMessage = this.initialError.asReadonly();
  readonly showAllCompanies = this.showAllCompaniesState.asReadonly();

  /**
   * The company the tree is drawn from, and the scope a search is run against.
   * With the toggle off that is the selected company, so the panel opens on the
   * branch the user is already working in.
   */
  readonly displayRootId = computed(() =>
    this.showAllCompaniesState()
      ? this.userCompanyId()
      : (this.selectedCompanyId() ?? this.userCompanyId()),
  );

  /** The company the application is currently switched to, once it is known. */
  readonly selectedCompany = computed<CompanyNode | null>(() => {
    const id = this.selectedCompanyId();
    return (id && this.nodes().get(id)) || null;
  });

  /** The company the tree is rooted at, once its node is known. */
  readonly displayRootCompany = computed<CompanyNode | null>(() => {
    const id = this.displayRootId();
    return (id && this.nodes().get(id)) || null;
  });

  readonly rows = computed(() => this.projectRows());

  /**
   * True while any branch appends a further page. Read from the branches rather
   * than the projected rows so it does not depend on what is currently visible.
   */
  readonly loadingMore = computed(() => {
    for (const branch of this.branches().values()) {
      if (branch.status === 'loading' && branch.children.length > 0) {
        return true;
      }
    }

    return false;
  });

  async initialize(): Promise<void> {
    this.reset();
    this.initialStatusState.set('loading');
    this.initialError.set(null);

    const userCompanyId = this.companyState.getUserCompanyId();
    const userCompany = this.companyState.getCopyOfUserCompany();
    if (!userCompanyId || !userCompany?.id) {
      this.initialError.set(MISSING_USER_COMPANY_ERROR);
      this.initialStatusState.set('error');
      return;
    }

    // Before the root node is cached: an exhausted snapshot clears the maps.
    const snapshot = this.cache.open(userCompanyId);

    this.userCompanyId.set(userCompanyId);
    this.cacheNode(this.toNode(userCompany, true));

    this.showAllCompaniesState.set(this.viewMode.read(userCompanyId));

    const selectedCompany = this.companyState.getCopyOfSelectedCompany();
    const selectedId = selectedCompany?.id ?? userCompanyId;
    this.selectedCompanyId.set(selectedId);

    if (this.showAllCompaniesState()) {
      const cachedPath =
        snapshot === 'empty'
          ? null
          : this.cachedAncestorPath(selectedId, userCompanyId);

      if (cachedPath) {
        // Synchronously, before the first await: a status set after one is a
        // change-detection pass too late and the panel flashes its spinner.
        this.selectedPath.set(cachedPath);
        this.applyExpansion(cachedPath, userCompanyId);
        this.initialStatusState.set('loaded');
      } else {
        const path = await this.resolveAncestorPath(
          selectedCompany,
          userCompanyId,
        );
        this.selectedPath.set(path);
        this.applyExpansion(path, userCompanyId);
      }
    } else {
      // Nothing sits above the display root to walk to or expand down through,
      // so the ancestor resolution is skipped whole. It was also the only thing
      // that cached the selected company's node, which the root row needs.
      if (selectedCompany?.id) {
        this.cacheNode(this.toNode(selectedCompany));
      }
      this.applyExpansion([], userCompanyId);
      if (snapshot !== 'empty') {
        this.initialStatusState.set('loaded');
      }
    }

    const displayRootId = this.displayRootId() ?? userCompanyId;

    try {
      await this.loadBranch(displayRootId);
    } catch (error) {
      this.initialError.set(
        this.branches().get(displayRootId)?.errorMessage ??
          this.listErrorHandler.processErrorCode('Companies', 'load', error),
      );
      this.initialStatusState.set('error');
      return;
    }

    const { restored, failed } =
      await this.loadVisibleExpandedBranches(displayRootId);
    // Only a walk that started at the user's own company reaches every
    // remembered branch. Pruning against one that started inside the hierarchy
    // would discard every expansion outside the display root's subtree.
    if (displayRootId === userCompanyId) {
      this.pruneUnrestoredExpanded(restored, failed);
    }

    // Only a real fetch stamps the snapshot. Stamping on a hit would push the
    // freshness window out on every opening and make it immortal.
    if (snapshot === 'empty') {
      this.cache.markLoaded();
    }

    this.initialStatusState.set('loaded');

    if (snapshot === 'stale') {
      void this.refreshSnapshot();
    }
  }

  /**
   * Re-roots the panel in place: the selection does not change and the panel
   * does not close.
   */
  async setShowAllCompanies(next: boolean): Promise<void> {
    const userCompanyId = this.userCompanyId();
    if (!userCompanyId || next === this.showAllCompaniesState()) {
      return;
    }

    this.showAllCompaniesState.set(next);
    this.viewMode.write(userCompanyId, next);

    if (!next) {
      // Normally already loaded, because the row was on screen. A selected
      // company sitting collapsed in the show-all tree has never had its
      // children fetched, and nothing else would ask for them once it is the
      // root.
      try {
        await this.loadBranch(this.displayRootId() ?? userCompanyId);
      } catch {
        // The row carries the failure and its own retry, as any branch does.
      }
      return;
    }

    // A second flip while the walk is in flight must not start another one; the
    // one already running resolves the same chain.
    if (this.resolvingAncestors) {
      return;
    }

    this.resolvingAncestors = true;
    try {
      const selectedId = this.selectedCompanyId() ?? userCompanyId;
      const path =
        this.cachedAncestorPath(selectedId, userCompanyId) ??
        (await this.resolveAncestorPath(
          this.companyState.getCopyOfSelectedCompany(),
          userCompanyId,
        ));

      this.selectedPath.set(path);
      this.applyExpansion(path, userCompanyId);

      // The panel is already on screen and `initialStatus` stays `loaded`:
      // flipping it back would blank a panel the user is looking at.
      await this.loadBranch(userCompanyId);
      await this.loadVisibleExpandedBranches(userCompanyId);
    } catch {
      return;
    } finally {
      this.resolvingAncestors = false;
    }
  }

  async toggle(id: string): Promise<void> {
    if (this.expanded().has(id)) {
      const next = new Set(this.expanded());
      next.delete(id);
      this.expanded.set(next);
      this.persistExpanded();
      return;
    }

    if (this.knownLeaves().has(id)) {
      return;
    }

    this.markExpanded(id);
    this.persistExpanded();

    try {
      await this.loadBranch(id);
    } catch {
      return;
    }
  }

  async loadMore(id: string): Promise<void> {
    try {
      await this.loadBranch(id, { append: true });
    } catch {
      return;
    }
  }

  async retry(id: string): Promise<void> {
    const existing = this.branches().get(id);
    if (existing?.status !== 'error') {
      return;
    }

    const append = existing.children.length > 0 && !!existing.cursor;
    try {
      await this.loadBranch(id, append ? { append: true } : undefined);
    } catch {
      return;
    }
  }

  async select(
    id: string,
    source: CompanySelectedSource,
    node?: CompanyNode,
  ): Promise<void> {
    const next = new Set(this.expanded());
    for (const ancestorId of this.ancestorsOf(id)) {
      next.delete(ancestorId);
      next.add(ancestorId);
    }
    this.expanded.set(next);
    this.persistExpanded();

    const known = this.nodes().get(id) ?? node;
    const userCompanyId = this.userCompanyId();
    this.tracker.companySelectedEvent(
      id,
      known?.name ?? '',
      id === userCompanyId,
      source,
    );

    try {
      await this.companyState.switchCompany(id);
    } catch (error) {
      console.error('Failed to load selected company.', error);
    }
  }

  private reset(): void {
    this.expanded.set(new Set());
    this.selectedPath.set([]);
    this.userCompanyId.set(null);
    this.selectedCompanyId.set(null);
    this.initialStatusState.set('idle');
    this.initialError.set(null);
    this.showAllCompaniesState.set(false);
  }

  private applyExpansion(path: readonly string[], userCompanyId: string): void {
    this.expanded.set(
      new Set<string>([
        ...this.expansion.read(userCompanyId),
        ...path,
        userCompanyId,
      ]),
    );
  }

  /**
   * The selected company's ancestor chain read out of the remembered nodes, or
   * null when any link is unknown and the API walk has to run instead.
   *
   * `ancestorsOf` cannot stand in for this: it breaks out on a missing node and
   * still prepends the root, reporting a partial chain as complete. That is
   * right for recording expansion on selection and wrong here, where a partial
   * chain would place the selection under the wrong branch.
   */
  private cachedAncestorPath(
    selectedId: string,
    userCompanyId: string,
  ): string[] | null {
    if (selectedId === userCompanyId) {
      return [];
    }

    let current = this.nodes().get(selectedId);
    if (!current) {
      return null;
    }

    const path: string[] = [];
    // A hierarchy that contains one of its own ancestors would otherwise walk
    // the same ids forever.
    const visited = new Set<string>([selectedId]);

    while (current.parentId && current.parentId !== userCompanyId) {
      const parentId = current.parentId;
      if (visited.has(parentId)) {
        break;
      }
      visited.add(parentId);

      const parent = this.nodes().get(parentId);
      if (!parent) {
        return null;
      }

      path.unshift(parentId);
      current = parent;
    }

    path.unshift(userCompanyId);
    return path;
  }

  /**
   * Re-requests the first page of the display root's branch and of every
   * expanded branch
   * that is already loaded, behind a panel that is already on screen. A refresh
   * the user did not ask for never shows a spinner and never turns a working
   * panel into an error state, so a failure leaves the remembered branch as it
   * was and simply leaves the snapshot stale for the next opening.
   */
  private async refreshSnapshot(): Promise<void> {
    const userCompanyId = this.userCompanyId();
    const rootId = this.displayRootId();
    if (!userCompanyId || !rootId || !this.cache.beginRefresh()) {
      return;
    }

    const ids = [
      rootId,
      ...[...this.expanded()].filter(
        (id) => id !== rootId && this.branches().get(id)?.status === 'loaded',
      ),
    ];

    let ok = true;
    for (const id of ids) {
      if (!this.cache.owns(userCompanyId)) {
        ok = false;
        break;
      }

      try {
        await this.loadBranch(id, { refresh: true });
      } catch {
        ok = false;
      }
    }

    this.cache.endRefresh(ok);
  }

  private async resolveAncestorPath(
    selected: CompanyLike | null | undefined,
    userCompanyId: string,
  ): Promise<string[]> {
    if (!selected?.id || selected.id === userCompanyId) {
      return [];
    }

    this.cacheNode(this.toNode(selected));

    const path: string[] = [];
    // A hierarchy that contains one of its own ancestors would otherwise keep
    // requesting the same companies and never resolve.
    const visited = new Set<string>([selected.id]);
    let current: CompanyLike = selected;

    while (current.parentId && current.parentId !== userCompanyId) {
      const parentId = current.parentId;
      if (visited.has(parentId)) {
        break;
      }
      visited.add(parentId);
      try {
        const parent = await this.companyApi.getCompany(parentId);
        this.cacheNode(this.toNode(parent));
        path.unshift(parent.id);
        current = parent;
      } catch (error) {
        this.handleAncestorFailure(error, parentId, userCompanyId);
        return path;
      }
    }

    path.unshift(userCompanyId);
    return path;
  }

  private handleAncestorFailure(
    error: unknown,
    parentId: string,
    userCompanyId: string,
  ): void {
    console.error('Failed to load company.', error);

    if (this.isForbiddenOrNotFound(error)) {
      this.expansion.prune(userCompanyId, parentId);
      return;
    }

    this.exceptionLogging.handle(error);
  }

  /**
   * Loads every persisted branch whose whole ancestor chain is expanded, level
   * by level, and reports the ids it reached alongside the ones whose load
   * failed. A failed branch is reached but never yields its children, so its
   * own persisted descendants go unvisited through no fault of the user.
   */
  private async loadVisibleExpandedBranches(
    rootId: string,
  ): Promise<{ restored: Set<string>; failed: Set<string> }> {
    // A hierarchy that contains one of its own ancestors would otherwise keep
    // repopulating the queue and never drain.
    const visited = new Set<string>([rootId]);
    const failed = new Set<string>();
    let levelIds = this.branches().get(rootId)?.children ?? [];

    while (levelIds.length > 0) {
      const expanded = this.expanded();
      const toLoad: string[] = [];
      for (const id of levelIds) {
        if (
          expanded.has(id) &&
          !visited.has(id) &&
          !this.knownLeaves().has(id)
        ) {
          visited.add(id);
          toLoad.push(id);
        }
      }

      await Promise.all(
        toLoad.map(async (id) => {
          try {
            await this.loadBranch(id);
          } catch {
            failed.add(id);
          }
        }),
      );

      const next: string[] = [];
      for (const id of toLoad) {
        next.push(...(this.branches().get(id)?.children ?? []));
      }
      levelIds = next;
    }

    return { restored: visited, failed };
  }

  /**
   * Drops persisted ids the restore never reached, because they sit inside a
   * collapsed ancestor. Kept, they would surface later as a branch that renders
   * expanded with nothing under it — its children were never loaded — so
   * opening it would take two clicks: one to collapse the stale state and one
   * to expand for real.
   *
   * Skipped entirely when any branch failed to load: an id under a failed
   * branch is indistinguishable here from one under a collapsed ancestor, and
   * pruning writes to storage, so a transient API error would permanently
   * discard an expansion the user chose. The stale-expansion cleanup is worth
   * forgoing in a session that already hit an error.
   */
  private pruneUnrestoredExpanded(
    restored: ReadonlySet<string>,
    failed: ReadonlySet<string>,
  ): void {
    if (failed.size > 0) {
      return;
    }

    const current = this.expanded();
    const next = new Set([...current].filter((id) => restored.has(id)));
    if (next.size === current.size) {
      return;
    }

    this.expanded.set(next);
    this.persistExpanded();
  }

  private async loadBranch(
    id: string,
    options?: { append?: boolean; refresh?: boolean },
  ): Promise<void> {
    const append = options?.append === true;
    const refresh = options?.refresh === true;
    const existing = this.branches().get(id);

    if (refresh) {
      if (existing?.status === 'loading') {
        return;
      }
    } else if (append) {
      if (!existing?.cursor || existing.status === 'loading') {
        return;
      }
    } else if (
      existing?.status === 'loading' ||
      existing?.status === 'loaded'
    ) {
      return;
    }

    if (!refresh) {
      this.setBranch(id, {
        status: 'loading',
        children: existing?.children ?? [],
        cursor: existing?.cursor ?? null,
      });
    }

    try {
      const page = await this.companyApi.getCompanies(
        {
          companyId: id,
          sortBy: 'name',
          count: BRANCH_PAGE_SIZE,
        },
        append ? (existing?.cursor ?? undefined) : undefined,
      );
      const items = page.items ?? [];
      const declaredLeaves: string[] = [];
      for (const item of items) {
        this.cacheNode(this.toNode(item));
        if (item.hasSubcompanies === false && item.id) {
          declaredLeaves.push(item.id);
        }
      }

      const childIds = append
        ? [...(existing?.children ?? []), ...items.map((item) => item.id)]
        : items.map((item) => item.id);

      if ((!append && items.length === 0) || declaredLeaves.length > 0) {
        this.knownLeaves.update((set) => {
          const next = new Set(set);
          if (!append && items.length === 0) {
            next.add(id);
          }
          for (const leafId of declaredLeaves) {
            next.add(leafId);
          }
          return next;
        });
      }

      this.setBranch(id, {
        status: 'loaded',
        children: childIds,
        cursor: page.cursor || page.nextPageToken || null,
      });
    } catch (error) {
      console.error('Failed to load companies.', error);
      if (refresh) {
        throw error;
      }
      if (this.isForbiddenOrNotFound(error)) {
        this.forgetRevokedBranch(id);
      }
      this.setBranchError(id, error);
      throw error;
    }
  }

  /**
   * Drops a branch the user no longer has access to from both storage and the
   * in-memory set. Storage alone is not enough: `persistExpanded` serializes the
   * whole set, so the next interaction would write the revoked id straight back.
   */
  private forgetRevokedBranch(id: string): void {
    this.expanded.update((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });

    const userCompanyId = this.userCompanyId();
    if (userCompanyId) {
      this.expansion.prune(userCompanyId, id);
    }
  }

  private setBranchError(id: string, error: unknown): void {
    const existing = this.branches().get(id);
    this.setBranch(id, {
      status: 'error',
      children: existing?.children ?? [],
      cursor: existing?.cursor ?? null,
      errorMessage: this.listErrorHandler.processErrorCode(
        'Companies',
        'load',
        error,
      ),
    });
  }

  private projectRows(): CompanyTreeRow[] {
    const rootId = this.displayRootId();
    if (!rootId || !this.nodes().get(rootId)) {
      return [];
    }

    const rows: CompanyTreeRow[] = [];
    this.visitNode(rootId, 1, 1, 1, rows, new Set<string>());
    return rows;
  }

  private visitNode(
    id: string,
    level: number,
    posInSet: number,
    setSize: number,
    rows: CompanyTreeRow[],
    ancestors: Set<string>,
  ): void {
    const node = this.renderableNode(id, ancestors);
    if (!node) {
      return;
    }

    // Expanded by projection rather than by writing the id into `expanded`:
    // that set is persisted, so a sub-company root would leak into the show-all
    // tree and into the next session as an expansion nobody chose.
    const expanded = id === this.displayRootId() || this.expanded().has(id);
    const branch = this.branches().get(id);
    const selectedId = this.selectedCompanyId();
    const selectedPath = this.selectedPath();

    rows.push({
      id: node.id,
      name: node.name,
      address: node.address,
      displayCount: node.displayCount,
      level,
      posInSet,
      setSize,
      variant: level === 1 ? 'root' : 'branch',
      expandable: !this.knownLeaves().has(id),
      expanded,
      selected: node.id === selectedId,
      containsSelected: selectedPath.includes(node.id) && !expanded,
      loading: branch?.status === 'loading' && branch.children.length === 0,
      loadingMore:
        expanded && branch?.status === 'loading' && branch.children.length > 0,
      errorMessage: branch?.errorMessage ?? null,
      hasMore: expanded && !!branch?.cursor,
    });

    if (!expanded) {
      return;
    }

    const children = branch?.children ?? [];
    ancestors.add(id);
    // Children this visit would skip emit no row, so counting them would
    // inflate setSize and shift every following sibling's posInSet. The
    // predicate must match the early return above, and it must run after
    // `ancestors.add(id)` so a self-referential child is excluded too.
    const renderableChildren = children.filter(
      (childId) => !!this.renderableNode(childId, ancestors),
    );
    // A branch with pages left to load has an unknown sibling count, which ARIA
    // spells -1 rather than the number loaded so far.
    const childSetSize = branch?.cursor ? -1 : renderableChildren.length;
    renderableChildren.forEach((childId, index) => {
      this.visitNode(
        childId,
        level + 1,
        index + 1,
        childSetSize,
        rows,
        ancestors,
      );
    });
    ancestors.delete(id);
  }

  /** The node a visit would render, or null when the visit would skip it. */
  private renderableNode(
    id: string,
    ancestors: Set<string>,
  ): CompanyNode | null {
    if (ancestors.has(id)) {
      return null;
    }

    return this.nodes().get(id) ?? null;
  }

  private toNode(company: CompanyLike, isRoot = false): CompanyNode {
    const node: CompanyNode = {
      id: company.id ?? '',
      name: company.name ?? '',
      parentId: isRoot ? null : (company.parentId ?? null),
      address: this.addressUtils.formatShortAddress(company),
    };

    // Absent is not zero: a company we were told nothing about must not claim
    // it has no displays.
    if (Number.isFinite(company.displayCount)) {
      node.displayCount = company.displayCount;
    }

    return node;
  }

  private cacheNode(node: CompanyNode): void {
    if (!node.id) {
      return;
    }

    this.nodes.update((map) => {
      const next = new Map(map);
      next.set(node.id, node);
      return next;
    });
  }

  private setBranch(id: string, state: BranchState): void {
    this.branches.update((map) => {
      const next = new Map(map);
      next.set(id, state);
      return next;
    });
  }

  private markExpanded(id: string): void {
    const next = new Set(this.expanded());
    next.delete(id);
    next.add(id);
    this.expanded.set(next);
  }

  private persistExpanded(): void {
    const userCompanyId = this.userCompanyId();
    if (!userCompanyId) {
      return;
    }

    this.expansion.write(userCompanyId, [...this.expanded()]);
  }

  private ancestorsOf(id: string): string[] {
    const userCompanyId = this.userCompanyId();
    if (!userCompanyId || id === userCompanyId) {
      return [];
    }

    const path: string[] = [];
    // A hierarchy that contains one of its own ancestors would otherwise walk
    // the same ids forever.
    const visited = new Set<string>([id]);
    let current = this.nodes().get(id);
    while (current?.parentId && current.parentId !== userCompanyId) {
      const parentId = current.parentId;
      if (visited.has(parentId)) {
        break;
      }
      visited.add(parentId);
      path.unshift(parentId);
      current = this.nodes().get(parentId);
      if (!current) {
        break;
      }
    }

    if (!path.includes(userCompanyId)) {
      path.unshift(userCompanyId);
    }

    return path;
  }

  private isForbiddenOrNotFound(error: unknown): boolean {
    const status = this.httpStatus(error);
    return status === 403 || status === 404;
  }

  private httpStatus(error: unknown): number | undefined {
    const extracted = getError(error);
    if (typeof extracted?.status === 'number') {
      return extracted.status;
    }

    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status: unknown }).status;
      return typeof status === 'number' ? status : undefined;
    }

    return undefined;
  }
}
